import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ModalPDF from "./ModalPDF"; // We reuse the modal wrapper
import DOMPurify from "dompurify";
import { getAyudaDetalle } from "../../api/ayudas/ayudasApi";
import { CircularProgress } from "@mui/material";

// Estilos extraídos exactamente de formulario.html adaptados a styled-components
const ArticleContainer = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;          /* Clave para que flex no expanda */
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 28px;
  
  /* Scrollbar estético */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
`;

const ArticleCard = styled.article`
  width: 100%;
  max-width: 100%;       /* No exceder nunca el padre */
  margin: 0 auto;
  font-family: var(--font-body);
  color: #0f172a;
  box-sizing: border-box;
  padding-bottom: 20px;
  overflow: hidden;      /* Corte absoluto de cualquier hijo rebelde */

  * {
    box-sizing: border-box;
    line-height: inherit;
  }
`;

const SHeader = styled.header`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 14px;
`;

const SBadge = styled.div`
  min-width: 84px;
  background: linear-gradient(180deg, #0f766e, #065f46);
  color: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: center;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(6, 95, 70, 0.12);

  small {
    display: block;
    font-weight: 600;
    font-size: 12px;
    opacity: 0.95;
    line-height: 1.1;
  }

  p {
    margin: 0;
  }
`;

const STitleArea = styled.div`
  flex: 1;

  h1 {
    margin: 0;
    font-size: 18px;
    letter-spacing: 0.2px;
  }

  .meta {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 12px;
  }

  .pill {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    background: #f3faf8;
    color: #065f46;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 13px;
    border: 1px solid rgba(6, 95, 70, 0.06);
    font-weight: 600;
  }
`;

const SMain = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  margin-top: 8px;
  max-width: 100%;
`;

const SSection = styled.section`
  padding: 14px;
  border-radius: 10px;
  background: linear-gradient(180deg, #ffffff, #fbfeff);
  border: 1px solid #e6eef0;
  overflow: hidden;      /* Impide cualquier escape horizontal */
  min-width: 0;

  h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #065f46;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .ql-editor {
    padding: 0;
  }

  /* Listas en Rich text */
  ul, ol {
    margin: 8px 0 0 0;
    padding-left: 20px;
    gap: 8px;
  }

  li {
    color: #0f172a; /* Mismo color sólido y fuerte que los párrafos */
    font-size: 14px;
    margin-bottom: 6px;
  }
`;

const STwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  max-width: 100%;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SFooter = styled.footer`
  margin-top: 24px;
  padding-bottom: 24px; /* Espaciador final crítico */
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .disclaimer {
    font-size: 12px;
    color: #6b7280;
  }

  .revision {
    font-size: 13px;
    color: #6b7280;
    strong {
      font-weight: bold;
    }
    span {
      font-weight: 600;
      color: #0f172a;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

// Renderizado purificado para prevenir XSS
const SafeHTMLWrapper = styled.div`
  width: 100%;
`;

const SafeHTML = ({ htmlContent }) => {
  if (!htmlContent) return null;
  // Reemplazar &nbsp; por espacios normales para permitir word-wrap natural
  const normalized = htmlContent.replace(/&nbsp;/gi, ' ').replace(/\u00A0/g, ' ');
  const clean = DOMPurify.sanitize(normalized);
  return <SafeHTMLWrapper dangerouslySetInnerHTML={{ __html: clean }} />;
};

const hasVisibleHtmlContent = (htmlContent) => {
  if (!htmlContent) return false;

  const normalized = String(htmlContent)
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized !== '';
};

const ActivityModal = ({ isOpen, onClose, activity, title }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isOpen || !activity) return;

    let cons = "";
    let sub = "";
    if (activity.includes("_")) {
      const parts = activity.split("_");
      cons = parts[0];
      sub = parts[1];
    } else {
      cons = activity;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await getAyudaDetalle(cons, "", sub);
        console.log("Respuesta de getAyudaDetalle (cons: " + cons + ", sub: " + sub + "):", response);
        if (response.data && Object.keys(response.data).length > 0) {
          setData(response.data);
        } else {
          setData(null);
        }
      } catch (err) {
        console.error("Error fetching Ayuda:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, activity]);

  return (
    <ModalPDF
      isOpen={isOpen}
      onClose={onClose}
      title="Ver detalles de la actividad"
      width="850px"
      minH="90vh"
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
          <CircularProgress />
        </div>
      ) : error ? (
        <EmptyState>Ocurrió un error al cargar la ayuda.</EmptyState>
      ) : !data ? (
        <EmptyState>Sin ayuda configurada.</EmptyState>
      ) : (
        <ArticleContainer>
          <ArticleCard>
            <SHeader>
              <SBadge>
                <small>
                  <p style={{ marginTop: 8 }}>Ayuda para el profesional</p>
                  <p>de la salud</p>
                </small>
                <div style={{ fontSize: 30, marginTop: 8 }}>ISA</div>
              </SBadge>

              <STitleArea>
                <h1>{data.titulo || title || "Actividad (Sin Título)"}</h1>
                <div className="meta">
                  {data.tags && data.tags.map((tag, i) => (
                    <div className="pill" key={i}>
                      <span>{tag}</span>
                    </div>
                  ))}
                  {(!data.tags || data.tags.length === 0) && (
                    <div className="pill">
                      <span>General</span>
                    </div>
                  )}
                </div>
              </STitleArea>
            </SHeader>

            <SMain>
              {hasVisibleHtmlContent(data.desc_richtext) && (
                <SSection>
                  <h3>Descripción General</h3>
                  <SafeHTML htmlContent={data.desc_richtext} />
                </SSection>
              )}

              {hasVisibleHtmlContent(data.cond_richtext) && (
                <SSection>
                  <h3>Condición y Criterio de Aplicación</h3>
                  <SafeHTML htmlContent={data.cond_richtext} />
                </SSection>
              )}

              {hasVisibleHtmlContent(data.puntos_richtext) && (
                <SSection>
                  <h3>Puntos Clave de la Intervención</h3>
                  <SafeHTML htmlContent={data.puntos_richtext} />
                </SSection>
              )}

              {hasVisibleHtmlContent(data.info_interv_richtext) && (
                <SSection>
                  <h3>Información de la Intervención</h3>
                  <SafeHTML htmlContent={data.info_interv_richtext} />
                </SSection>
              )}

              {(hasVisibleHtmlContent(data.freq_richtext) || hasVisibleHtmlContent(data.excep_richtext)) && (
                <STwoCol>
                  {hasVisibleHtmlContent(data.freq_richtext) && (
                    <SSection>
                      <h3>Frecuencia y Periodicidad</h3>
                      <SafeHTML htmlContent={data.freq_richtext} />
                    </SSection>
                  )}

                  {hasVisibleHtmlContent(data.excep_richtext) && (
                    <SSection>
                      <h3>Excepciones y Contraindicaciones</h3>
                      <SafeHTML htmlContent={data.excep_richtext} />
                    </SSection>
                  )}
                </STwoCol>
              )}

              {hasVisibleHtmlContent(data.source_rich) && (
                <SSection>
                  <h3>Fuente</h3>
                  <SafeHTML htmlContent={data.source_rich} />
                </SSection>
              )}
            </SMain>

            <SFooter>
              <div className="disclaimer">Información para uso exclusiva de personal de salud.</div>
              {(data.fecha_revision || data.ultima_revision) && (
                <div className="revision">
                  Última revisión <strong>·</strong> <span>{data.fecha_revision ? data.fecha_revision : data.ultima_revision.split(' ')[0]}</span>
                </div>
              )}
            </SFooter>
          </ArticleCard>
        </ArticleContainer>
      )}
    </ModalPDF>
  );
};

export default ActivityModal;
